// src/utils/firestoreUser.js
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Ensures a user document exists in Firestore.
 * Creates it if it doesn't, or updates `lastLoginAt` and potentially other
 * mutable fields (like name or email) if it does.
 *
 * CRITICAL NOTE ON USER IDs:
 * The `id` parameter passed to this function MUST EXACTLY MATCH the `uid`
 * that Firebase Authentication uses for the signed-in user. This `uid` is
 * what `request.auth.uid` will be in your Firestore security rules.
 *
 * Your `server.js` currently creates Firebase custom tokens with `clerk:${clerkUserId}` as the UID.
 * However, your `App.js` calls this function with `user.id` from Clerk (e.g., "user_xyz...").
 * This mismatch (`clerk:user_xyz...` vs `user_xyz...`) WILL CAUSE PERMISSION ERRORS.
 *
 * To fix this, you have two main options:
 * 1. (Recommended for simplicity if Clerk is sole auth) Modify `server.js`:
 *    Change `admin.auth().createCustomToken(firebaseUid)` to use the raw Clerk User ID
 *    as the `firebaseUid`. So, `const firebaseUid = clerkUser;` instead of `const firebaseUid = \`clerk:\${clerkUser}\`;`.
 * 2. Modify `App.js`:
 *    After `signInToFirebaseWithClerk()`, get `auth.currentUser.uid` from Firebase Auth
 *    and pass *that* Firebase UID to this `createUserIfNeeded` function.
 *
 * @param {object} userData - User data.
 * @param {string} userData.id - The user's unique ID (MUST match Firebase Auth UID after resolving the mismatch).
 * @param {string} userData.email - The user's email.
 * @param {string} userData.fullName - The user's full name.
 * @returns {Promise<boolean>} True if the operation was successful (or user already existed), false on error.
 */
export const createUserIfNeeded = async ({ id, email, fullName }) => {
  if (!id) {
    // console.warn('[bootstrap] User ID is missing. Cannot create/update user doc.', { id, email, fullName });
    return false;
  }
  // Email is important, but let's allow proceeding if ID is present, storing null for email if missing.
  if (!email) {
    // console.warn('[bootstrap] User email is missing. Will store null for email.', { id, fullName });
  }

  // console.log(`[bootstrap] Ensuring user doc for ID: ${id}`);
  const userRef = doc(db, 'users', id);

  try {
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // console.log('[bootstrap] User doc does not exist. Creating new one for ID:', id);
      const newUserDoc = {
        uid: id, // Store the UID explicitly
        email: email || null,
        name: fullName || 'Unnamed User', // Fallback for name
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        approved: false,
      };
      await setDoc(userRef, newUserDoc);
      // console.log(`[bootstrap] ✅ Created user doc for: ${id}`);
    } else {
      // console.log(`[bootstrap] ℹ️ User doc already exists for: ${id}. Updating lastLoginAt.`);
      // Optionally, update other fields like name or email if they might change
      // and you want to keep them synced from Clerk.
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        // email: email || null, // Uncomment if you want to update email on every login
        // name: fullName || 'Unnamed User', // Uncomment if you want to update name on every login
      });
      // console.log(`[bootstrap] ✅ Successfully updated user doc for: ${id}`);
    }
    return true;

  } catch (err) {
    const errorMessage = err.message || 'Unknown error';
    const errorCode = err.code || 'N/A';
    // console.error(`[bootstrap] ❌ Failed to create/update user doc for: ${id}. Code: ${errorCode}, Message: ${errorMessage}`, err);
    return false;
  }
};